import { useState } from 'react';
import { Link } from 'react-router';
import Button from '../ui/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../pilot-ui/dropdown-menu';
import { Popover, PopoverContent, PopoverDescription, PopoverTitle, PopoverTrigger } from '../pilot-ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../pilot-ui/tabs';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList, ComboboxTrigger } from '../pilot-ui/combobox';
import { Label } from '../pilot-ui/label';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../pilot-ui/tooltip';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from '../pilot-ui/sheet';
import { ToggleGroup, ToggleGroupItem } from '../pilot-ui/toggle-group';

const regions = ['Northeast', 'Midwest', 'South', 'West'];

export default function PatternsPage() {
  const [notice, setNotice] = useState('No pattern action selected.');
  const [region, setRegion] = useState<string | null>(null);
  const [exampleState, setExampleState] = useState<'loaded' | 'empty' | 'error'>('loaded');

  return (
    <main className="mx-auto max-w-5xl space-y-6 p-6">
      <h1 className="text-2xl font-bold">Console control patterns</h1>
      <p className="text-sm text-muted-foreground">Synthetic examples for system administrators. Changes here do not affect devices or accounts.</p>
      <p role="status" className="rounded-lg border border-border bg-muted p-3 text-sm">{notice}</p>
      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Sample fleet state</h2>
        <p className="text-sm text-muted-foreground">Compare how a synthetic list explains available data, an empty result, and a loading failure.</p>
        <div className="flex flex-wrap gap-2">
          {(['loaded', 'empty', 'error'] as const).map((state) => (
            <Button key={state} variant={exampleState === state ? 'primary' : 'secondary'} onClick={() => setExampleState(state)} title={`Show the ${state} state for this synthetic fleet example.`}>
              Show {state} example
            </Button>
          ))}
        </div>
        {exampleState === 'loaded' && <p>Three synthetic fleet groups are available.</p>}
        {exampleState === 'empty' && <p>No sample fleet groups match this view.</p>}
        {exampleState === 'error' && <p role="alert">Sample fleet groups could not be loaded. Try again.</p>}
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Device actions menu</h2>
        <p className="text-sm text-muted-foreground">Open this menu to choose a read-only action for a sample fleet group.</p>
        <DropdownMenu>
          <DropdownMenuTrigger render={<Button variant="secondary" title="Show actions for the synthetic Northeast fleet group." />}>Fleet group actions</DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem title="Show a summary action for the synthetic fleet group." onClick={() => setNotice('Sample fleet summary selected.')}>View fleet summary</DropdownMenuItem>
            <DropdownMenuItem title="Show a device-list action for the synthetic fleet group." onClick={() => setNotice('Sample device list selected.')}>View devices</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Path details popover</h2>
        <p className="text-sm text-muted-foreground">Inspect what a sample entity path grants without leaving this page.</p>
        <Popover>
          <PopoverTrigger render={<Button variant="secondary" title="Show details about the sample entity path." />}>Inspect path</PopoverTrigger>
          <PopoverContent>
            <PopoverTitle>/demo/northeast/</PopoverTitle>
            <PopoverDescription>Devices under this path are visible to accounts granted this scope. This path is synthetic.</PopoverDescription>
          </PopoverContent>
        </Popover>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Fleet view tabs</h2>
        <p className="text-sm text-muted-foreground">Switch between summary and access notes for a sample fleet.</p>
        <Tabs defaultValue="summary">
          <TabsList aria-label="Sample fleet views">
            <TabsTrigger value="summary" title="Read the number of synthetic regions.">Summary</TabsTrigger>
            <TabsTrigger value="access" title="Read how scope grants control fleet visibility.">Access</TabsTrigger>
          </TabsList>
          <TabsContent value="summary">Four synthetic regions are available for this demo.</TabsContent>
          <TabsContent value="access">An account needs a matching device scope to see a region.</TabsContent>
        </Tabs>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 id="pattern-region-heading" className="text-lg font-semibold">Region combobox</h2>
        <p id="pattern-region-help" className="text-sm text-muted-foreground">Open the region list, type to filter the synthetic regions, then choose one with Enter or a click.</p>
        <Label htmlFor="pattern-region-trigger" className="block text-sm font-medium">Find a sample region</Label>
        <Combobox items={regions} value={region} onValueChange={setRegion}>
          <ComboboxTrigger id="pattern-region-trigger" aria-describedby="pattern-region-help" title="Open the searchable list of synthetic regions." className="flex w-full items-center justify-between rounded-md border bg-background px-3 py-2 text-sm">
            {region ?? 'Choose a sample region'}
          </ComboboxTrigger>
          <ComboboxContent aria-labelledby="pattern-region-heading">
            <Label htmlFor="pattern-region-search" className="block px-2 pt-2 text-sm font-medium">Search regions</Label>
            <ComboboxInput id="pattern-region-search" showTrigger={false} placeholder="Search regions" />
            <ComboboxEmpty>No matching region.</ComboboxEmpty>
            <ComboboxList>{(item: string) => <ComboboxItem key={item} value={item}>{item}</ComboboxItem>}</ComboboxList>
          </ComboboxContent>
        </Combobox>
        <p className="text-sm">Selected: {region ?? 'none'}</p>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Help tooltip</h2>
        <p className="text-sm text-muted-foreground">Focus or hover the help control for a short explanation.</p>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger render={<Button variant="icon" aria-label="What is a device scope?" title="Explain how a device scope grants visibility." />}>?</TooltipTrigger>
            <TooltipContent>A device scope grants visibility to devices under an entity path.</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Device detail sheet</h2>
        <p className="text-sm text-muted-foreground">Open a side panel with a synthetic device summary.</p>
        <Sheet>
          <SheetTrigger render={<Button variant="secondary" title="Open the synthetic device detail panel." />}>View sample device</SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>Demo sensor</SheetTitle>
              <SheetDescription>Synthetic device in the Northeast region. No live data is shown.</SheetDescription>
            </SheetHeader>
          </SheetContent>
        </Sheet>
      </section>

      <section className="space-y-2 rounded-xl border border-border bg-card p-4">
        <h2 className="text-lg font-semibold">Metric toggle group</h2>
        <p id="metric-help" className="text-sm text-muted-foreground">Choose which sample metric a fleet chart would show.</p>
        <ToggleGroup variant="outline" aria-label="Sample chart metric" aria-describedby="metric-help" defaultValue={['pressure']}>
          <ToggleGroupItem value="pressure" aria-label="Pressure" title="Show sample pressure readings.">Pressure</ToggleGroupItem>
          <ToggleGroupItem value="temperature" aria-label="Temperature" title="Show sample temperature readings.">Temperature</ToggleGroupItem>
          <ToggleGroupItem value="runtime" aria-label="Runtime" title="Show sample runtime readings.">Runtime</ToggleGroupItem>
        </ToggleGroup>
      </section>
      <Link to="/system" className="inline-block text-sm text-blue-700 underline" title="Return to the System settings page.">
        Return to System settings
      </Link>
    </main>
  );
}
